import { useLazyQuery, useMutation } from '@apollo/client';
import { AutoCompleteTalentProfessions } from '@components/AutoComplete/AutoCompleteTalentProfessions';
import FormContainer from '@components/FormContainer';
import { fetchLatLongFromAddress, fetchLatLongFromCityPostalCode } from '@components/InputCity/hereApi';
import InputText from '@components/InputFieldControlled';
import InputGender from '@components/InputGender';
import InputMultiSelect from '@components/InputMultiSelect';
import InputPostal from '@components/InputPostal';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_OPTIONS_TALENT } from '@gql/options';
import { GET_TALENT, UPDATE_ADMINISTRATIVE_TALENT } from '@gql/talents';
import { Grid, Stack } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

type Props = {
    talent?: any;
    onValidition?: (data?: any) => void;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    onBack?: () => void;
    onFormCancel?: () => void;
    isEdit?: boolean;
    byCV?: boolean;
    initialFormState?: 'view' | 'edit';
};

const TalentForm = React.forwardRef<any, Props>((props: Props, ref) => {
    const [options, setOptions] = useState({
        groupProfessions: [],
        preferences: [],
        degree: [],
        sectors: [],
    });
    const [loading, setLoading] = useState(false);

    const [fetch] = useLazyQuery(GET_OPTIONS_TALENT, {
        onCompleted: result => {
            setOptions(result?.options);
        },
    });
    const talent = props.talent;

    const defaultValues = useMemo(() => ({
        firstName: talent?.firstName,
        lastName: talent?.lastName,
        gender: talent?.gender,
        phoneNumber: talent?.phoneNumber?.replace(/^\b0+/g, '').replace(/\s/g, ''),
        bio: talent?.bio,
        preferences: talent?.preferences,
        postalCode: talent?.postalCode,
        city: talent?.city,
        country: talent?.country ?? 'France',
        address: talent?.address,
        principalProfession: talent?.principalProfession,
        professions: [talent?.principalProfession, ...(talent?.professions ?? [])].filter(Boolean),
        currentSalary: talent?.currentSalary,
        expectedSalary: talent?.expectedSalary,
        social: {
            instagram: talent?.social?.instagram,
            linkedin: talent?.social?.linkedin,
            website: talent?.social?.website,
        },
    }), [talent]);

    const {
        control,
        handleSubmit,
        watch,
        getValues,
        setValue,
        reset,
        trigger,
        formState: { isDirty },
    } = useForm({
        mode: 'all',
        defaultValues,
    });

    const principalProfession = watch('principalProfession');

    const { showSnackbar } = useSnackbar();

    const [update] = useMutation(UPDATE_ADMINISTRATIVE_TALENT, {
        onCompleted: () => {
            const data = getValues();
            props?.onSuccess?.(data);
            setLoading(false);
            showSnackbar('Profil mis à jour avec succès', 'success');
        },
        onError: error => {
            props?.onError?.(error);
            setLoading(false);
            showSnackbar('Erreur lors de la mise à jour du profil', 'error');
        },
    });

    const submitHandler = useCallback(
        async data => {
            let result = await fetchLatLongFromAddress(
                `${data.address}, ${data.postalCode}, ${data.city}, ${data.country}`,
            );
            if (!result?.items?.length) {
                result = await fetchLatLongFromCityPostalCode(data.city, data.postalCode);
            }
            setLoading(true);
            await update({
                variables: {
                    payload: {
                        ...data,
                        professions: (data.professions || []).filter(
                            profession => profession && profession !== data.principalProfession,
                        ),
                        location: {
                            type: 'Point',
                            coordinates: [result?.items?.[0]?.position?.lng, result?.items?.[0]?.position?.lat],
                        },
                        currentSalary: parseFloat(data.currentSalary),
                        expectedSalary: parseFloat(data.expectedSalary),
                        // if no changes while updating, take the default value, the phoneNumber value sent from the Input of 'FR +33123123123' , while from the API it's split into its own item
                        phoneNumber: data.phoneNumber,
                    },
                    talentId: talent?._id,
                },
                refetchQueries: () => [
                    {
                        query: GET_TALENT,
                        variables: { talentId: talent?._id },
                    },
                ],
            });
        },
        [update, talent?._id],
    );

    useEffect(() => {
        fetch();
    }, [fetch]);

    const groupedProfessions = useMemo(
        () =>
            (options?.groupProfessions || []).map(group => ({
                ...group,
                children: (group?.children || []).map(child => {
                    const childValue = child?.value ?? child?.id;
                    return {
                        ...child,
                        isFixed: principalProfession === childValue,
                    };
                }),
            })),
        [options?.groupProfessions, principalProfession],
    );

    const [formState, setFormState] = useState<'view' | 'edit'>('view');

    useEffect(() => {
        // reset the form when the talent changes
        if (props.talent) {
            reset(defaultValues);
        }
    }, [props.talent, reset, defaultValues]);

    // register trigger so it can triggered by parent component
    React.useImperativeHandle(ref, () => ({
        submit: async () => {
            const isValid = await trigger();
            if (isValid && isDirty) {
                await handleSubmit(submitHandler)();
            }
            return isValid;
        },
    }));

    return (
        <FormContainer
            loading={loading}
            formState={'edit'}
            setFormState={setFormState}
            onCancel={() => {
                reset();
                props.onFormCancel?.();
            }}
            onSubmit={handleSubmit(submitHandler)}
            formTitle="Modifiez votre profil">
            <>
                <InputGender label="Genre" name="gender" control={control} required />
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <InputText
                        name="firstName"
                        control={control}
                        label={formState === 'view' ? 'Nom complet' : 'Prénom'}
                        placeholder="Prénom"
                        required
                    />
                    {formState === 'edit' && (
                        <InputText name="lastName" control={control} label="Nom" placeholder="Nom" required />
                    )}
                </Stack>
                <InputPostal
                    label={'Code postal'}
                    name="postalCode"
                    control={control}
                    keyboardType="phone-pad"
                    placeholder="Code postal"
                    required
                    onSelect={function (city: string): void {
                        setValue('city', city);
                    }}
                />
                <InputText name="city" disabled control={control} label="Ville" placeholder="Ville" />
                <InputText name="country" disabled control={control} label="Pays" placeholder="Pays" />
                <InputText name="address" control={control} label="Adresse" placeholder="Adresse" />
                <Grid container spacing={1}>
                    <Grid item xs={12} >
                        <InputMultiSelect
                            control={control}
                            name="preferences"
                            label="Contrat souhaité"
                            placeholder="Ex : CDI"
                            groupedOptions={options?.preferences?.map(item => {
                                return {
                                    label: item.name,
                                    options: item.children?.map(child => ({
                                        ...child,
                                        label: child.name,
                                        value: child.id,
                                    })),
                                };
                            })}
                            required
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AutoCompleteTalentProfessions
                            allowAdd
                            control={control}
                            name="professions"
                            label="Métier recherché"
                            placeholder="Sélectionnez votre métier"
                            multiple={true}
                            groupedOptions={groupedProfessions}
                            defaultValue={defaultValues?.professions}
                        />
                    </Grid>
                </Grid>

            </>
        </FormContainer>
    );
});

export default TalentForm;
